import Uppy, { UploadResult } from '@uppy/core';
import Webcam from '@uppy/webcam';
import { AxiosError } from 'axios';
import { forEach } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { ordersApi } from 'api';
import { useSciDomain } from 'components';
import { EventName, LanguageCode, OrderStatus, QueryKey } from 'enums';
import { useLanguage } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Order, OrderReceipt } from 'types';
import { eventUtils, gaUtils } from 'utils';

const DEFAULT_UPPY_LANGUAGE = 'en_US';

const MAP_LANGUAGE_TO_UPPY_LOCALE: Record<Partial<LanguageCode>, string> = {
  [LanguageCode.English]: DEFAULT_UPPY_LANGUAGE,
  [LanguageCode.Russian]: 'ru_RU',
  [LanguageCode.Korean]: 'ko_KR',
  [LanguageCode.Spanish]: 'es_ES',
  [LanguageCode.Hindi]: 'hi_IN',
  [LanguageCode.Turkish]: 'tr_TR',
  [LanguageCode.Ukrainian]: 'uk_UA',
  [LanguageCode.Uzbek]: 'uz_UZ',
  [LanguageCode.Armenian]: 'hy_AM',
  [LanguageCode.Azerbaijani]: 'az_AZ',
  [LanguageCode.Kyrgyz]: 'ky_KG',
  [LanguageCode.Kazakh]: 'kk_KZ',
  [LanguageCode.Tajik]: 'tg_TJ',
};

const uppy = new Uppy({
  autoProceed: false,
  allowMultipleUploads: false,
  restrictions: {
    maxNumberOfFiles: 1,
    allowedFileTypes: ['image/*', 'application/pdf'],
    maxFileSize: 1024 * 1024 * 3,
  },
}).use(Webcam);

type UseOrderReceiptsInput = {
  orderId: string;
  order: Order | undefined;
  onApiError: (error: AxiosError) => void;
};

export type UseOrderReceiptsResult = {
  receipts: OrderReceipt[] | undefined;
  isReceiptsLoading: boolean;
  canManageReceipts: boolean;
  hasPendingFile: boolean;
  isUploadingReceipt: boolean;
  uploadReceiptModalOpen: boolean;
  setUploadReceiptModalOpen: (open: boolean) => void;
  uppy: Uppy;
  setReceiptUploadCallbacks: (
    onUpload: () => void,
    onClose: () => void,
  ) => void;
  telegramSupportBotUrl: string | undefined;
  handleTelegramOrderHelp: () => void;
  handleTelegramUploadReceipt: () => void;
};

export const useOrderReceipts = ({
  orderId,
  order,
  onApiError,
}: UseOrderReceiptsInput): UseOrderReceiptsResult => {
  const { t: tUploadReceipt } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.upload_confirmation_receipt',
  });

  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const { data: sciDomainConfig } = useSciDomain();
  const telegramSupportBotUrl = sciDomainConfig?.telegramSupportBotUrl;

  const uploadCallbacksRef = useRef<{
    onUpload: () => void;
    onClose: () => void;
  } | null>(null);

  const [uploadReceiptModalOpen, setUploadReceiptModalOpen] = useState(false);
  const [hasPendingFile, setHasPendingFile] = useState(false);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

  useEffect(() => {
    const onFileAdded = () => setHasPendingFile(true);
    const onFileRemoved = () => setHasPendingFile(uppy.getFiles().length > 0);
    const onUploadStart = () => setIsUploadingReceipt(true);

    uppy.on('file-added', onFileAdded);
    uppy.on('file-removed', onFileRemoved);
    uppy.on('upload', onUploadStart);

    return () => {
      uppy.off('file-added', onFileAdded);
      uppy.off('file-removed', onFileRemoved);
      uppy.off('upload', onUploadStart);
    };
  }, []);

  const canManageReceipts = useMemo(
    () =>
      [OrderStatus.Cancelled, OrderStatus.Dispute].includes(
        order?.status as OrderStatus,
      ) && !!order?.requisites,
    [order],
  );

  useEffect(() => {
    if (!canManageReceipts) {
      uppy.getFiles().forEach((file) => uppy.removeFile(file.id));
    }
  }, [canManageReceipts]);

  const { data: receipts, isLoading: isReceiptsLoading } = useQuery(
    QueryKey.OrderReceipts,
    () => ordersApi.getOrderReceipts(orderId),
    {
      enabled: canManageReceipts,
      retry: false,
      onError: onApiError,
    },
  );

  const uploadModalLocaleOverride = useMemo(
    () => ({
      dropPasteImportFiles: tUploadReceipt('description'),
      uploadXFiles: {
        0: tUploadReceipt('submit'),
      },
    }),
    [tUploadReceipt],
  );

  const { mutate: uploadPaymentReceipt } = useMutation(ordersApi.uploadReceipt);

  const handleReceiptRemove = useCallback(() => {
    forEach(uppy.getFiles(), (file) => uppy.removeFile(file.id));
  }, []);

  useEffect(() => {
    const handleComplete = (result: UploadResult<Record<string, unknown>>) => {
      if (!uploadCallbacksRef.current) {
        return;
      }

      const file = result.successful[0];
      const formData = new FormData();
      formData.append('file', file.data, file.name);

      const { onUpload, onClose } = uploadCallbacksRef.current;

      uploadPaymentReceipt(
        { orderId, body: formData },
        {
          onSuccess: () => {
            eventUtils.trackEvent({ event: EventName.ReceiptUpload });
            queryClient.invalidateQueries(QueryKey.OrderReceipts);
            handleReceiptRemove();
            onUpload();
            onClose();
            setIsUploadingReceipt(false);
          },
          onError: (e: unknown) => {
            const error = e as AxiosError<{ message: string | undefined }>;
            const message = error?.response?.data?.message || error?.message;
            eventUtils.trackEvent({
              event: EventName.ReceiptUploadError,
              message,
            });
            uppy.info(message, 'error');
            uppy.resetProgress();
            setIsUploadingReceipt(false);
          },
        },
      );
    };

    uppy.on('complete', handleComplete);
    return () => {
      uppy.off('complete', handleComplete);
    };
  }, [orderId, uploadPaymentReceipt, queryClient, handleReceiptRemove]);

  useEffect(() => {
    const loadLocale = async () => {
      let locale;
      try {
        const localeCode = MAP_LANGUAGE_TO_UPPY_LOCALE[language];
        locale = await import(`@uppy/locales/lib/${localeCode}`);
      } catch (e: any) {
        const message = 'Language loading failed: ' + e.message;
        gaUtils.trackException({ fatal: false, description: message });
        eventUtils.trackException({ message });
        locale = await import(`@uppy/locales/lib/${DEFAULT_UPPY_LANGUAGE}`);
      } finally {
        uppy.setOptions({
          locale: {
            strings: {
              ...locale.default.strings,
              ...uploadModalLocaleOverride,
            },
          },
        });
      }
    };

    loadLocale();
  }, [language, uploadModalLocaleOverride]);

  const setReceiptUploadCallbacks = useCallback(
    (onUpload: () => void, onClose: () => void) => {
      uploadCallbacksRef.current = { onUpload, onClose };
    },
    [],
  );

  const handleTelegramOrderHelp = useCallback(() => {
    gaUtils.trackEvent(EventName.TelegramHelp);
    window.open(`${telegramSupportBotUrl}?start=order_${orderId}`, '_blank');
  }, [orderId, telegramSupportBotUrl]);

  const handleTelegramUploadReceipt = useCallback(() => {
    gaUtils.trackEvent(EventName.TelegramUploadReceipt);
    window.open(`${telegramSupportBotUrl}?start=receipt`, '_blank');
  }, [telegramSupportBotUrl]);

  return {
    receipts,
    isReceiptsLoading,
    canManageReceipts,
    hasPendingFile,
    isUploadingReceipt,
    uploadReceiptModalOpen,
    setUploadReceiptModalOpen,
    uppy,
    setReceiptUploadCallbacks,
    telegramSupportBotUrl,
    handleTelegramOrderHelp,
    handleTelegramUploadReceipt,
  };
};
